import {
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { OrganizationSidebarLayout } from '../../../organizations/components/organization-sidebar-layout/organization-sidebar-layout';
import { PageHeader } from '../../../layout/components/page-header/page-header';
import { PageTitle } from '../../../layout/components/page-title/page-title';
import {
  Button,
  SimpleDialog,
  NotificationService,
  InputSimple,
  Select,
  SimpleDialogLayout,
  TooltipDirective,
} from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { LocationsStore } from '../../../locations/store/locations.store';
import { ActivatedRoute, Router } from '@angular/router';
import { EnergyInterfaceCreateDialog } from '../../dialogs/energy-interface-create-dialog/energy-interface-create-dialog';
import { EnergyInterfacesCreateSuccessNotification } from '../../notifications/energy-interfaces-create-success-notification/energy-interfaces-create-success-notification';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';
import { EnergyInterfacesStore } from '../../stores/energy-interfaces.store';
import { FormField, form } from '@angular/forms/signals';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { EnergyInterfaceLocationSkeleton } from '../../components/energy-interface-location-skeleton/energy-interface-location-skeleton';
import { LocationNamePipe } from '../../../locations/pipes/location-name-pipe';
import { EnergyInterfacesEmptyBox } from '../../components/energy-interfaces-empty-box/energy-interfaces-empty-box';
import { EnergyInterfaceLocation } from '../../components/energy-interface-location/energy-interface-location';
import { EnergyInterfaceListNoResults } from '../../components/energy-interface-list-no-results/energy-interface-list-no-results';
import { EnergyInterfaceListSection } from '../../components/energy-interface-list-section/energy-interface-list-section';
import { EnergyInterfaceList } from '../../components/energy-interface-list/energy-interface-list';
import { EnergyInterfaceListEntry } from '../../components/energy-interface-list-entry/energy-interface-list-entry';
import { TutorialEnergyInterfacesPopover } from '../../../tutorials/components/tutorial-energy-interfaces-popover/tutorial-energy-interfaces-popover';
import { VideoTutorialService } from '../../../tutorials/services/video-tutorial.service';

@Component({
  selector: 'app-energy-interfaces-page',
  imports: [
    OrganizationSidebarLayout,
    PageHeader,
    PageTitle,
    Button,
    SimpleDialog,
    NgIcon,
    TranslocoPipe,
    EnergyInterfaceCreateDialog,
    InputSimple,
    Select,
    LocationNamePipe,
    FormField,
    ErrorPage,
    EnergyInterfaceLocationSkeleton,
    SimpleDialogLayout,
    EnergyInterfacesEmptyBox,
    EnergyInterfaceLocation,
    EnergyInterfaceListNoResults,
    EnergyInterfaceListSection,
    EnergyInterfaceList,
    EnergyInterfaceListEntry,
    TooltipDirective,
    TutorialEnergyInterfacesPopover,
  ],
  templateUrl: './energy-interfaces-page.html',
})
export class EnergyInterfacesPage implements OnInit {
  private videoTutorialService = inject(VideoTutorialService);
  private notificationService = inject(NotificationService);
  private organizationsStore = inject(OrganizationsStore);
  private locationsStore = inject(LocationsStore);
  private energyInterfacesStore = inject(EnergyInterfacesStore);
  private activatedRoute = inject(ActivatedRoute);
  protected router = inject(Router);

  organizationId = input.required<string>();

  private tutorialPopover = viewChild(TutorialEnergyInterfacesPopover);
  private createEnergyInterfaceDialog = viewChild<SimpleDialog>('createEnergyInterfaceDialog');

  protected locations = computed(() => this.locationsStore.filteredLocations() ?? []);
  protected filteredLocations = computed(() => {
    const locations = this.locations();
    if (!this.energyInterfacesStore.isFilterActive()) {
      return locations;
    }
    return locations.filter((location) =>
      this.energyInterfacesStore
        .filteredEnergyInterfaces()
        ?.some((ei) => ei.data?.locationID === location.id),
    );
  });
  protected loading = computed(
    () => this.locationsStore.isLoading() || this.energyInterfacesStore.isLoading(),
  );
  protected loadError = computed(
    () => this.locationsStore.error() || this.energyInterfacesStore.error(),
  );

  protected selectedLocation = signal<string | null>(null);

  protected filter = linkedSignal(() => ({
    query: this.energyInterfacesStore.filterQuery(),
    utilityType: this.energyInterfacesStore.filterUtilityType(),
    locationId: this.energyInterfacesStore.filterLocationId(),
  }));

  protected filterForm = form(this.filter);

  protected locationLessEnergyInterfaces = computed(
    () =>
      this.energyInterfacesStore.filteredEnergyInterfaces()?.filter((ei) => !ei.data?.locationID) ??
      [],
  );

  constructor() {
    effect(() => {
      const popover = this.tutorialPopover();
      if (popover && this.videoTutorialService.value()?.LumoTutorialEnergyInterfaces === false) {
        popover.showPopover();
        this.videoTutorialService.markTutorialAsViewed('LumoTutorialEnergyInterfaces');
      }
    });
  }

  ngOnInit(): void {
    const locationID = this.activatedRoute.snapshot.queryParamMap.get('locationID');
    const utilityType = this.activatedRoute.snapshot.queryParamMap.get('utilityType');
    const query = this.activatedRoute.snapshot.queryParamMap.get('query');
    if (locationID || utilityType || query) {
      this.filter.set({ locationId: locationID, utilityType, query });
      this.onChangeFilter();
    }
  }

  protected onClickAddEnergyInterface(location: string | null): void {
    this.selectedLocation.set(location);
    this.createEnergyInterfaceDialog()?.showModal();
  }

  protected onCreateSuccess(energyInterfaceId: string) {
    this.createEnergyInterfaceDialog()?.close();
    this.selectedLocation.set(null);
    const label = this.energyInterfacesStore
      .energyInterfaces()
      ?.find((ei) => ei.id === energyInterfaceId)?.data?.label;
    this.notificationService.show({
      component: EnergyInterfacesCreateSuccessNotification,
      duration: 5000,
      componentProps: {
        organizationId: this.organizationsStore.selectedId(),
        energyInterfaceId,
        energyInterfaceLabel: label,
      },
    });
  }

  protected onChangeFilter() {
    const { query, utilityType, locationId } = this.filter();
    this.energyInterfacesStore.setFilter(query, utilityType, locationId);
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        query: query,
        utilityType: utilityType,
        locationID: locationId,
      },
      queryParamsHandling: 'replace',
    });
  }

  protected onClickResetFilter() {
    this.filter.set({ query: null, utilityType: null, locationId: null });
    this.onChangeFilter();
  }
}
